import { TextField, Box } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import { useSelector } from '../../../../../redux/store';

// Wrap the component with forwardRef
const CategorySelector = forwardRef(
  (
    {
      isCategory,
      currentCategory,
      currentSubcategory,
      currentGlCode,
      existingGlCodes,
      disabled,
      handleChange,
      error,
      index,
      textFieldProps,
      variant = 'standard',
      item,
      facilityId,
    },
    ref // <-- ref comes as the second argument
  ) => {
    const {
      data: {
        invoiceData: { categories: cats, manualCategories, subcategories: subcats, manualSubcategories },
        invoiceItemData,
      },
    } = useSelector((state) => state.invoicing);

    const manualInvoice = invoiceItemData?.invoice?.sourceMethod === 'Manual';
    const categories = !manualInvoice ? cats : manualCategories || [];
    const subcategories = !manualInvoice ? subcats : manualSubcategories || [];

    // const getFilteredSubcategories = (facilitySubcategories, currentGlCode) =>
    //   facilitySubcategories?.filter((sub) => {
    //     console.log('filtering subcategory', sub, currentGlCode);
    //     const isDifferentGlCode = currentGlCode !== sub.glCode;
    //     const isNotInExistingGlCodes = !existingGlCodes?.some(
    //       (existing) => existing.glCode === sub.glCode && existing.facilityId === sub.facilityId
    //     );
    //     return sub.glCode !== null && (isDifferentGlCode ? isNotInExistingGlCodes : true);
    //   });

    const filterOptions = createFilterOptions({
      stringify: (option) => `${option.category} (${option.description})`,
    });
    const facilityCategories = categories.filter((cat) => cat?.facilityId === facilityId);
    const facilitySubcategories = subcategories.filter((sub) => sub?.facilityId === facilityId);

    const nonDependantGls = facilitySubcategories.some((x) => x.categoryId === 0);
    const subcategoriesOptions =
      manualInvoice || currentCategory === 0
        ? facilitySubcategories
        : facilitySubcategories.filter((x) => x.categoryId === 0 || x.categoryId === currentCategory);
    return (
      <>
        {isCategory ? (
          <Autocomplete
            key={`${currentCategory}-${facilityCategories.length}`}
            sx={{ flexGrow: 1 }}
            slotProps={{
              popper: { sx: { minWidth: 'max-content', width: 'auto' } },
              paper: {
                sx: { border: '1px solid #00000049', '& .MuiAutocomplete-option': { fontSize: '12px' } },
              },
            }}
            options={facilityCategories}
            filterOptions={filterOptions}
            getOptionLabel={(option) => `${option.category}${option.description ? ` (${option.description})` : ''}`}
            onChange={(event, newValue) => handleChange(newValue, item, index, nonDependantGls)}
            disabled={disabled}
            value={currentCategory ? facilityCategories?.find((x) => x.categoryId === currentCategory) : null}
            renderOption={(props, option) => (
              <li {...props} style={{ fontSize: '12px' }}>
                {option.category} {option.description ? `(${option.description})` : ''}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={ref} // <-- attach ref to the actual input
                variant={variant}
                error={error}
                autoComplete="off"
                InputProps={{
                  ...params.InputProps,
                  ...textFieldProps?.InputProps,

                  sx: { fontSize: '12px', '& .MuiInputBase-input': { fontSize: '12px', color: '#060606' } },
                }}
              />
            )}
          />
        ) : (
          <Autocomplete
            key={`${currentSubcategory}-${subcategoriesOptions.length}`}
            sx={{ flexGrow: 1 }}
            options={
              !manualInvoice
                ? subcategoriesOptions
                : [...subcategoriesOptions, { value: 'add-gl-code', glCode: 'Add GL Code' }]
            }
            slotProps={{
              popper: { sx: { minWidth: 'max-content', width: 'auto' } },
              paper: {
                sx: {
                  border: '1px solid #00000049',
                  '& .MuiAutocomplete-listbox': { padding: 0 },
                  '& .MuiAutocomplete-option': { fontSize: '12px' },
                },
              },
            }}
            noOptionsText="Add Gl Code"
            getOptionLabel={(option) => (`${option.glCode} ${option.subcategory ? `(${option.subcategory})` : ''}`)}
            onChange={(event, newValue) => handleChange(newValue, item, index, nonDependantGls)}
            value={
              currentSubcategory ? subcategoriesOptions?.find((x) => x.subcategoryId === currentSubcategory) : null
            }
            disabled={disabled}
            disableClearable
            renderOption={(props, option) =>
              option.value === 'add-gl-code' ? (
                <Box
                  component="li"
                  {...props}
                  key={option.value}
                  role="button"
                  tabIndex={0}
                  sx={{
                    textDecoration: 'underline',
                    color: '#1976d2',
                    cursor: 'pointer',
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: '#fff',
                    zIndex: 10,
                    fontSize: '12px',
                    borderTop: '1px solid #00000020',

                    '&.MuiAutocomplete-option': {
                      backgroundColor: '#fff',
                    },

                    '&.MuiAutocomplete-option:hover': {
                      backgroundColor: '#fff',
                    },

                    '&.MuiAutocomplete-option.Mui-focused': {
                      backgroundColor: '#fff',
                    },

                    '&.MuiAutocomplete-option[aria-selected="true"]': {
                      backgroundColor: '#fff',
                    },
                  }}
                >
                  {option.glCode}
                </Box>
              ) : (
                <Box component="li" {...props} key={option.value} sx={{ fontSize: '12px' }}>
                  {option.glCode} {option.subcategory ? `(${option.subcategory})` : ''}
                </Box>
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={ref} // <-- attach ref here too
                variant={variant}
                error={error}
                autoComplete="off"
                InputProps={{
                  ...params.InputProps,
                  ...textFieldProps?.InputProps,
                  sx: { fontSize: '12px', '& .MuiInputBase-input': { fontSize: '12px', color: '#060606' } },
                }}
              />
            )}
          />
        )}
      </>
    );
  }
);

CategorySelector.propTypes = {
  isCategory: PropTypes.bool,
  currentCategory: PropTypes.any,
  currentSubcategory: PropTypes.any,
  currentGlCode: PropTypes.any,
  existingGlCodes: PropTypes.any,
  handleChange: PropTypes.func,
  index: PropTypes.number,
  error: PropTypes.any,
  variant: PropTypes.string,
  textFieldProps: PropTypes.any,
  item: PropTypes.object,
  facilityId: PropTypes.number,
  disabled: PropTypes.bool,
};

export default CategorySelector;
